import React, { useState } from 'react';
import { 
  Bell, 
  X, 
  Check, 
  CheckCheck, 
  Trash2,
  Sparkles,
  Cloud,
  Heart,
  UserPlus,
  Calendar,
  Luggage,
  Settings
} from 'lucide-react';
import { useNotifications, Notification } from '@/hooks/useNotifications';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const NOTIFICATION_ICONS: Record<string, React.ElementType> = {
  daily_outfit: Sparkles,
  weather_alert: Cloud,
  new_like: Heart,
  new_follower: UserPlus,
  calendar_reminder: Calendar,
  packing_reminder: Luggage,
  style_tip: Sparkles,
  default: Bell,
};

export function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const { 
    notifications, 
    preferences, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    updatePreferences,
    requestPushPermission,
  } = useNotifications();
  const [showSettings, setShowSettings] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-16 md:pt-24 bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md max-h-[80vh] overflow-hidden rounded-3xl border bg-card border-border shadow-2xl animate-in slide-in-from-top-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-aura-gold text-[10px] font-bold flex items-center justify-center text-foreground">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <h2 className="text-lg font-display">Notifications</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Settings size={18} className="text-muted-foreground" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {showSettings ? (
          /* Settings View */
          <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
            <div>
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">
                Notification Preferences
              </h3>
              
              <div className="space-y-4">
                <PreferenceToggle
                  label="Daily Outfit Suggestions"
                  description="Get personalized outfit ideas every morning"
                  checked={preferences.daily_outfit}
                  onChange={(checked) => updatePreferences({ daily_outfit: checked })}
                  icon={Sparkles}
                />
                <PreferenceToggle
                  label="Weather Alerts"
                  description="Get notified about weather changes"
                  checked={preferences.weather_alerts}
                  onChange={(checked) => updatePreferences({ weather_alerts: checked })}
                  icon={Cloud}
                />
                <PreferenceToggle
                  label="Social Activity"
                  description="Likes, follows, and comments"
                  checked={preferences.social_activity}
                  onChange={(checked) => updatePreferences({ social_activity: checked })}
                  icon={Heart}
                />
                <PreferenceToggle
                  label="Packing Reminders"
                  description="Trip preparation alerts"
                  checked={preferences.packing_reminders}
                  onChange={(checked) => updatePreferences({ packing_reminders: checked })}
                  icon={Luggage}
                />
                <PreferenceToggle
                  label="Style Tips"
                  description="Expert fashion advice"
                  checked={preferences.style_tips}
                  onChange={(checked) => updatePreferences({ style_tips: checked })}
                  icon={Sparkles}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">
                Push Notifications
              </h3>
              <Button
                onClick={requestPushPermission}
                variant={preferences.push_enabled ? "outline" : "default"}
                className="w-full"
                disabled={preferences.push_enabled}
              >
                {preferences.push_enabled ? (
                  <>
                    <Check size={16} className="mr-2" /> Push Enabled
                  </>
                ) : (
                  <>
                    <Bell size={16} className="mr-2" /> Enable Push Notifications
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Receive alerts even when the app is closed
              </p>
            </div>
          </div>
        ) : (
          /* Notifications List */
          <div className="overflow-y-auto max-h-[60vh]">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="w-full py-3 px-6 text-xs font-bold uppercase tracking-widest text-aura-gold hover:bg-aura-gold/10 transition-colors border-b border-border flex items-center justify-center gap-2"
              >
                <CheckCheck size={14} /> Mark all as read
              </button>
            )}
            
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell size={40} className="mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground text-sm">No notifications yet</p>
                <p className="text-xs text-muted-foreground/60 mt-1">
                  Stay active to receive updates!
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={() => markAsRead(notification.id)}
                    onDelete={() => deleteNotification(notification.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: () => void;
  onDelete: () => void;
}

function NotificationItem({ notification, onMarkAsRead, onDelete }: NotificationItemProps) {
  const Icon = NOTIFICATION_ICONS[notification.type] || NOTIFICATION_ICONS.default;
  
  return (
    <div
      className={cn(
        "p-4 flex gap-4 transition-colors hover:bg-muted/50",
        !notification.is_read && "bg-aura-gold/5"
      )}
    >
      <div className={cn(
        "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
        notification.is_read ? "bg-muted" : "bg-aura-gold/20"
      )}>
        <Icon size={18} className={notification.is_read ? "text-muted-foreground" : "text-aura-gold"} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4 className={cn(
            "text-sm",
            !notification.is_read && "font-medium"
          )}>
            {notification.title}
          </h4>
          {!notification.is_read && (
            <div className="w-2 h-2 rounded-full bg-aura-gold flex-shrink-0 mt-1.5" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-[10px] text-muted-foreground/60 mt-1">
          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
        </p>
      </div>
      
      <div className="flex flex-col gap-1">
        {!notification.is_read && (
          <button
            onClick={onMarkAsRead}
            className="p-1.5 hover:bg-muted rounded-lg transition-colors"
            title="Mark as read"
          >
            <Check size={14} className="text-muted-foreground" />
          </button>
        )}
        <button
          onClick={onDelete}
          className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-lg transition-colors"
          title="Delete"
        >
          <Trash2 size={14} className="text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}

interface PreferenceToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon: React.ElementType;
}

function PreferenceToggle({ label, description, checked, onChange, icon: Icon }: PreferenceToggleProps) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
          <Icon size={16} className="text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
